<template>
  <div>
    <ExpandableItemCard
      :title="item?.name || 'New Proprietary Blend'"
      :subtitle="item?.amount ? `(${item?.amount}${item?.unit ? ' ' + item?.unit : ''})` : ''"
      item-label="blend"
      :index="index"
      :total-items="totalItems"
      :start-expanded="startExpanded"
      :expanded="expanded"
      :active="active"
      :has-warning="!!missingAmountWarning || !!missingNameWarning"
      margin-bottom="mb-3"
      @toggle="$emit('toggle')"
    >
      <template #header-title>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="formattedBlendName"></span>
      </template>
      <template #header-actions>
        <button
          type="button"
          class="delete-header-btn"
          aria-label="Remove this blend"
          @click.stop="handleRemove"
        >
          <i class="fas fa-trash" aria-hidden="true"></i>
        </button>
      </template>
      <template #header-subtitle>
        <AmountWithUnitInput
          :amount="item?.amount"
          :unit="item?.unit"
          :uid="item?._uid"
          :allowed-units="BLEND_TOTAL_UNITS"
          class="is-small header-amount-input"
          placeholder="Amt"
          @update:amount="handleAmountInput"
          @update:unit="handleUnitInput"
          @click.stop
        />
      </template>
      <div class="field is-flex is-align-items-start mb-2">
        <label :for="`blend-name-${item?._uid}`" class="label is-small mb-0 mr-2 label-inline">
          Name
          <IconHint :content="HINT_TEXTS.CUSTOM_FORMATTING" />
        </label>
        <div class="control is-flex-grow-1">
          <input
            :id="`blend-name-${item?._uid}`"
            type="text"
            class="input is-small blend-name-input"
            :value="item?.name"
            name="blend"
            placeholder="e.g. Energy Blend"
            @input="update({ name: $event.target.value })"
          />
          <div class="checkbox-group mt-1">
            <label v-if="hasParens" class="checkbox is-size-7 is-flex is-align-items-center">
              <input
                type="checkbox"
                class="mr-1"
                :checked="item?.italicize_parenthetical"
                data-test-id="blend-italicize-checkbox"
                @change="update({ italicize_parenthetical: $event.target.checked })"
              />
              <span>{{ italicizeLabel }}</span>
            </label>
            <label
              v-if="(item?.display_mode || 'inline') !== 'inline'"
              class="checkbox is-size-7 is-flex is-align-items-center"
            >
              <input
                type="checkbox"
                class="mr-1"
                :checked="item?.hide_dagger"
                data-test-id="blend-hide-dagger-checkbox"
                @change="update({ hide_dagger: $event.target.checked })"
              />
              <span>{{ hideDaggerLabel }}</span>
            </label>
            <ExtraTextField
              :model-value="item?.extra_text"
              :is-expanded="hasExtraText || showExtraTextInput"
              checkbox-test-id="blend-extra-text-checkbox"
              input-test-id="blend-extra-text-input"
              @toggle="toggleExtraText"
              @update:model-value="updateExtraText"
            />
            <label class="checkbox is-size-7 is-flex is-align-items-center">
              <input
                type="checkbox"
                class="mr-1"
                :checked="hasFootnote"
                data-test-id="blend-footnote-checkbox"
                @change="toggleFootnote($event.target.checked)"
              />
              <span>Footnote</span>
            </label>
          </div>
          <FootnoteItemEditor
            :item="item"
            placeholder="e.g., Proprietary herbal complex"
            bordered
            @update="update($event)"
          />
        </div>
      </div>

      <div class="field is-flex is-align-items-start mb-2">
        <label
          :for="`blend-source-${item?._uid}`"
          class="label is-small mb-0 mr-2 label-inline pt-1"
        >
          Source
          <IconHint :content="HINT_TEXTS.CUSTOM_FORMATTING" />
        </label>
        <div class="control is-flex-grow-1">
          <input
            :id="`blend-source-${item?._uid}`"
            type="text"
            class="input is-small"
            :value="item?.source"
            name="source"
            placeholder="e.g. Plant based"
            data-test-id="blend-source-input"
            @input="update({ source: $event.target.value })"
          />
        </div>
      </div>

      <div class="field is-flex is-align-items-start mb-2">
        <label
          :for="`blend-amount-${item?._uid}`"
          class="label is-small mb-0 mr-2 label-inline pt-1"
          >Total Amount</label
        >
        <div class="control is-expanded">
          <AmountWithUnitInput
            :input-id="`blend-amount-${item?._uid}`"
            :amount="item?.amount"
            :unit="item?.unit"
            :uid="item?._uid"
            :allowed-units="BLEND_TOTAL_UNITS"
            @update:amount="handleAmountInput"
            @update:unit="handleUnitInput"
          />
        </div>
      </div>

      <div class="field is-flex is-align-items-start mb-2">
        <label
          :for="`blend-display-mode-${item?._uid}`"
          class="label is-small mb-0 mr-2 label-inline pt-1"
          >Display Mode</label
        >
        <div class="control is-expanded">
          <div class="select is-small is-fullwidth">
            <select
              :id="`blend-display-mode-${item?._uid}`"
              :key="`display_mode-${item?._uid}`"
              :value="item?.display_mode || 'inline'"
              data-test-id="blend-display-mode-select"
              @change="update({ display_mode: $event.target.value })"
            >
              <option
                v-for="opt in store.blendDisplayModeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <ConstituentEditor
          :constituents="item?.constituents || []"
          :blend-index="index"
          add-button-full-width
          @update:constituents="update({ constituents: $event })"
        />
      </div>

      <div v-if="missingNameWarning">
        <p class="notification is-warning is-light bordered-left py-2 px-3 mt-2">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          {{ missingNameWarning }}
        </p>
      </div>
      <div v-if="missingAmountWarning">
        <p class="notification is-warning is-light bordered-left py-2 px-3 mt-2">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          {{ missingAmountWarning }}
        </p>
      </div>
    </ExpandableItemCard>

    <ConfirmDialog
      v-model="showDeleteConfirm"
      title="Remove Blend"
      message="Are you sure you want to remove this blend?"
      confirm-text="Remove"
      :show-dont-ask-again="true"
      @confirm="doRemove"
      @dont-ask-again="setSkipDeleteConfirm"
    />
  </div>
</template>

<script setup>
  import { computed, toRef, inject } from "vue";
  import { useListItem } from "./composables/use-list-item";
  import ExpandableItemCard from "./expandable-item-card.vue";
  import AmountWithUnitInput from "./amount-with-unit-input.vue";
  import ConstituentEditor from "./blend-constituent-editor.vue";
  import ConfirmDialog from "../shared/confirm-dialog.vue";
  import { formatLabelName } from "./utils/formatting";
  import FootnoteItemEditor from "./footnote-item-editor.vue";
  import { useItemRemove } from "./composables/use-item-remove";
  import { useFootnoteExtraText } from "./composables/use-footnote-extra-text";
  import { useFormattingLabels } from "./composables/use-formatting-labels";
  import { BLEND_TOTAL_UNITS, HINT_TEXTS } from "./utils/constants";
  import IconHint from "../icon-hint.vue";
  import ExtraTextField from "./extra-text-field.vue";

  const props = defineProps({
    uid: { type: Number, required: true },
    startExpanded: { type: Boolean, default: false },
    expanded: { type: Boolean, default: undefined },
    active: { type: Boolean, default: false },
    editing: { type: Boolean, default: false },
  });

  const emit = defineEmits(["removed", "toggle"]);
  const triggerSave = inject("triggerSave");

  const { store, item, index, totalItems, update, remove } = useListItem(
    "blends",
    toRef(props, "uid")
  );

  const { hasParens, italicizeLabel, hideDaggerLabel } = useFormattingLabels(item);

  const formattedBlendName = computed(() => {
    const name = item.value?.name || "New Proprietary Blend";
    return formatLabelName(name, {
      italicizeParenthetical: item.value?.italicize_parenthetical,
      disableSubscripts: true,
    });
  });

  const missingNameWarning = computed(() => {
    if (props.editing) return null;
    if (item.value?.name) return null;
    // Only warn when the blend has some content — don't nag on a freshly-added empty card
    const hasContent =
      item.value?.amount ||
      (item.value?.constituents && item.value.constituents.some((c) => c.name));
    if (!hasContent) return null;
    return "A blend name is required.";
  });

  const missingAmountWarning = computed(() => {
    if (props.editing) return null;
    if (item.value?.amount) return null;
    if (!item.value?.name) return null;
    return store.validationWarnings.missing_amounts?.blends?.[index.value] || null;
  });

  const {
    hasFootnote,
    hasExtraText,
    showExtraTextInput,
    toggleFootnote,
    toggleExtraText,
    updateExtraText,
  } = useFootnoteExtraText(item, update, { onSave: triggerSave });

  function handleAmountInput(value) {
    update({ amount: value });
  }

  function handleUnitInput(value) {
    update({ unit: value });
  }

  const { showDeleteConfirm, handleRemove, doRemove, setSkipDeleteConfirm } = useItemRemove({
    cookieName: "skip_blend_delete_1h",
    remove,
    triggerSave,
    onRemoved: (index) => emit("removed", index),
  });
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_mixins";
</style>
